import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { pauseDecomUsers, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../../utils/roles/client-roles';
import { DecomSuspendedUserModel } from '../../../../../types';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface PauseSuspendedUsersDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
  userList: DecomSuspendedUserModel[];
}

export default function PauseSuspendedUsersDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  userList,
}: PauseSuspendedUsersDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');

  const { mutate, isPending } = useMutation({
    mutationFn: () => pauseDecomUsers(selectedModels, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The selected users decom processing has been paused.'
            : `The selected users' decom processing have been paused.`,
      });
      const updatedModels: DecomSuspendedUserModel[] = selectedModels.map(
        (model) => {
          return {
            ...model,
            isactive: false,
          };
        }
      );
      const filteredList = userList.filter(
        (user) => !selectedRows.includes(user.userId + user.creationDate)
      );
      const updatedList = [...filteredList, ...updatedModels];
      queryClient.setQueryData([UserKeys.SuspendedUsers], updatedList);
      handleClose();
      setSelectedRows([]);
      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue pausing the selected users decom processing. Please refresh and try again.'
            : `There was an issue pausing the selected users' decom processing. Please refresh and try again.`,
      });
    },
    onSettled: () => {
      createEvent(
        analytics.automation.decommissioning.pause_suspended_users_decom,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.decommissioning,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.PauseDecomForUser.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const selectedModels = userList.filter((user) =>
    selectedRows.includes(user.userId + user.creationDate)
  );

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={`Pause Decommissioning ${
        selectedRows.length === 1 ? 'User' : 'Users'
      }`}
      dialogContent={
        <Stack gap={2}>
          {selectedRows.length > 1 && (
            <Typography>
              Pause decommissioning process for{' '}
              <strong>{selectedRows.length} users</strong>.
            </Typography>
          )}
          {selectedRows.length === 1 && (
            <Typography>
              Pause decommissioning process for{' '}
              <strong>{selectedModels[0].email}</strong>.
            </Typography>
          )}
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: `Pause ${selectedRows.length === 1 ? 'User' : 'Users'}`,
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
      }}
    />
  );
}
